import {Platform} from 'react-native';
import {AppActions, AppThunk} from '..';
import {storage, StorageKeys} from '../../../shared/storage';
import {profileSlice} from '../../reducers/profile';
import {httpService, HttpWrapperOptions} from '../../../shared/httpWrapper';
import {apiUrls} from '../../../shared/constants/urls';
import {getApiRoute, hashPassword, onlyDigitsPipe} from '../../../shared/pipes';
import {loginSlice} from '../../reducers/login';
import {setRuntimeLoginStepState} from '../runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {
  AuthorizeCabinetRequestPayload,
  AuthorizeResponsePayload,
  CheckCodeRequestPayload,
  LoginResponsePayload,
  RequestedOperationType,
  SendCodeChannelType,
  SendCodeRequestPayload,
  SendCodeResponsePayload,
  SendCodeWithCheckPhoneResponsePayload,
  SetPasswordRequestPayload,
  SignOutRequestPayload,
} from '../../../shared/models/api';
import {fcmService} from '../../../shared/fcmWrapper';
import {getTokenResponse} from '../../../shared/fcmWrapper/model';
import {contentSlice} from '../../reducers/resources/content';
import {EsiaResponseType} from '../../../shared/models/login';
import {loginStepSlice} from '../../reducers/runtime/loginStep';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {AuthorizeEsiaRequestPayload} from '../../../shared/models/api/authorizeEsiaRequestPayload';
import {RegisterClientCabinetRequestPayload} from '../../../shared/models/api/registerClientCabinetRequestPayload';
import {addNotification} from '../snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import {setBadgeCount} from '../../../shared/fcmWrapper/pushNotification';
import {
  RegisterCardApplicationRequestPayload,
  RegisterCardApplicationResponsePayload,
} from '../../../types/registerCardApplicationRequestPayload';
import {ActivateCardRequestPayload} from '../../../types/activateCardRequestPayload';
import {CardStatus} from '../../../types/enums/cardStatus';

export const setLoginResponse = (response: LoginResponsePayload): AppThunk => {
  return (dispatch) => {
    dispatch(loginSlice.actions.setLoginResponse(response));
  };
};

export const setLoginValue = (login: string): AppThunk => {
  return (dispatch) => {
    dispatch(loginSlice.actions.setLogin(login));
  };
};

export const checkLogin = (login: string): AppThunk => {
  return async (dispatch) => {
    let response: LoginResponsePayload | null = null;
    response = await httpService.get<LoginResponsePayload>(
      getApiRoute(apiUrls.login, {login: login || ''}),
      new HttpWrapperOptions().Errors(false).ThrowException(true).Wait(true),
    );
    dispatch(loginSlice.actions.setLogin(login));
    dispatch(setLoginResponse(response));
    dispatch(loginSlice.actions.setLoginResponse(response));
    dispatch(loginSlice.actions.setSendCodeResponse(null));
  };
};

export const login = (password: string): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const authResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.auth),
      {
        login: onlyDigitsPipe(state.login.login ?? ''),
        loginType: state.login.loginResponse?.loginType,
        passHash,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
      } as AuthorizeCabinetRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (authResponse) {
      await storage.setItem(StorageKeys.token, authResponse.token);
      await storage.removeItem(StorageKeys.isESIA);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(authResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const registration = (password: string, code: string): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passwordHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const newPasswordResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.newPass),
      {
        passwordHash,
        code,
        codeId: state.login.sendCodeResponse?.codeId,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
      } as SetPasswordRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (newPasswordResponse) {
      await storage.setItem(StorageKeys.token, newPasswordResponse.token);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(newPasswordResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const createNewPassword = (code: string, password: string, isNewPhoneRegistration: boolean): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passwordHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const newPasswordResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.newPass),
      {
        passwordHash,
        code,
        codeId: state.login.sendCodeResponse?.codeId,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
        requestedOperation: isNewPhoneRegistration
          ? RequestedOperationType.SetPhoneNumberConfirmation
          : RequestedOperationType.SetPasswordConfirmation,
      } as SetPasswordRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (newPasswordResponse) {
      await storage.setItem(StorageKeys.token, newPasswordResponse.token);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(newPasswordResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const createNewPinCode = (pinCode: string): AppThunk => {
  return async (dispatch) => {
    await storage.setItem(StorageKeys.pinCode, pinCode);
    dispatch(profileSlice.actions.setPinCode(pinCode));
  };
};

export const logout = (): AppThunk => {
  return async (dispatch) => {
    let fcmToken: string | null = null;
    try {
      fcmToken = (await fcmService.getToken()).token;
    } catch {
      fcmToken = await storage.getItem<string>(StorageKeys.fcmToken);
    }
    try {
      if (fcmToken) {
        await httpService.post<void>(
          getApiRoute(apiUrls.signOut),
          {
            firebaseId: fcmToken,
          } as SignOutRequestPayload,
          new HttpWrapperOptions().Errors(false).ThrowException(true).Wait(true),
        );
        await fcmService.cleanToken();
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({type: AppActions.Reset});
      dispatch(profileSlice.actions.reset(null));
      await storage.removeItem(StorageKeys.pinCode);
      await storage.removeItem(StorageKeys.isESIA);
      await storage.removeItem(StorageKeys.activationToken);
      dispatch(profileSlice.actions.setSessionState(false));
      await storage.removeItem(StorageKeys.token);
      // await storage.removeItem(StorageKeys.api);
      await dispatch(contentSlice.actions.reset(null));
      setBadgeCount(0);
    }
  };
};

export const enter = (token: string): AppThunk => {
  return async (dispatch) => {
    dispatch(profileSlice.actions.setToken(token));
  };
};

export const resetLoginState = (): AppThunk => {
  return async (dispatch) => {
    dispatch(loginSlice.actions.reset(null));
  };
};

export const getNewCode = (numCardESK: string, showStandardError: boolean, channel: SendCodeChannelType): AppThunk => {
  return async (dispatch) => {
    const response = await httpService.post<SendCodeResponsePayload>(
      getApiRoute(apiUrls.sendCode),
      {
        channel,
        requestedOperation: RequestedOperationType.SetPasswordConfirmation,
        numCardESK,
      } as SendCodeRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(showStandardError).ThrowException(true),
    );
    if (response) {
      dispatch(loginSlice.actions.setSendCodeResponse(response));
      if (response?.codeId && channel === SendCodeChannelType.Email) {
        await storage.setItem(StorageKeys.recoveryCodeId, response?.codeId);
      }
    }
  };
};

export const checkCode = (code: string, mobilePhone: string, requestedOperation: RequestedOperationType): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    await httpService.post(
      getApiRoute(apiUrls.checkCode),
      {
        codeId: state.login.sendCodeResponse?.codeId,
        code,
        requestedOperation,
        mobilePhone,
      } as CheckCodeRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(false).ThrowException(true),
    );
  };
};

export const sendCodeWithCheckPhone = (
  cardNum: string,
  snils: string,
  mobilePhone: string,
  showStandardError: boolean,
): AppThunk => {
  return async (dispatch) => {
    const response = await httpService.post<SendCodeWithCheckPhoneResponsePayload>(
      getApiRoute(apiUrls.sendCodeWithCheckPhone),
      {
        cardNum,
        snils,
        mobilePhone,
      } as SendCodeWithCheckPhoneResponsePayload,
      new HttpWrapperOptions().Wait(true).Errors(showStandardError).ThrowException(true),
    );
    if (response && response.codeId) {
      dispatch(loginSlice.actions.setSendCodeResponse(response));
    }
  };
};

export const getEsiaLink = ({
  showError = true,
  loaderText = '',
}: {showError?: boolean; loaderText?: string} = {}): AppThunk => {
  return async (dispatch) => {
    try {
      const response = await httpService.get<{link: string}>(
        getApiRoute(apiUrls.esiaGetLink),
        new HttpWrapperOptions()
          .Errors(showError)
          .ThrowException(showError)
          .WaitTimeout(0)
          .Wait(true)
          .AddText(loaderText),
      );
      if (!response.link) throw new Error();
      dispatch(loginSlice.actions.setEsiaLink(response.link));
      // dispatch(
      //   loginSlice.actions.setEsiaLink(
      //     response.link.replace(
      //       'redirect_uri=https%3A%2F%2Ftest-lk.eskso.ru',
      //       'redirect_uri=http%3A%2F%2Flocalhost:3000',
      //     ),
      //   ),
      // );
      dispatch(setRuntimeLoginStepState(LoginState.esiaLogin));
    } catch (e) {
      dispatch(
        addNotification({
          message: 'Не удалось получить ссылку ЕСИА',
          type: SnackbarNotificationType.Error,
        }),
      );
      dispatch(loginSlice.actions.setEsiaLink(null));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
    }
  };
};

export const esiaLogin = (): AppThunk => {
  return async (dispatch, getState) => {
    const {code, state} = getState().login.esiaCode;
    let fcmToken: getTokenResponse | null = null;
    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.log(error);
    }
    const data: AuthorizeEsiaRequestPayload = {
      code: code!,
      state: state!,
      firebaseId: fcmToken?.token ?? null,
      previousFirebaseId: fcmToken?.previousToken ?? null,
    };

    try {
      const res = await httpService.post<AuthorizeEsiaResponsePayload>(
        getApiRoute(apiUrls.esiaLogin),
        data,
        new HttpWrapperOptions().WaitTimeout(0).Wait(true).ThrowException(true),
      );
      switch (res?.type) {
        case EsiaResponseType.Authorization:
          if (
            res.cardStatusId == null ||
            res.cardStatusId === CardStatus.InDelivery ||
            res.cardStatusId === CardStatus.Arrived ||
            res.cardStatusId === CardStatus.Reserved
          ) {
            await storage.setItem(StorageKeys.activationToken, res.token);
            await storage.setItem(StorageKeys.isESIA, true);
            dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
            dispatch(loginSlice.actions.setEsiaUserHasNoCards(true));
            dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
          } else {
            await storage.setItem(StorageKeys.token, res.token);
            await storage.setItem(StorageKeys.isESIA, true);
            if (Platform.OS === 'web') {
              dispatch(profileSlice.actions.setAuthenticationState(true));
            } else {
              dispatch(profileSlice.actions.setPinCode(null));
            }
            dispatch(loginSlice.actions.setEsiaUserHasNoCards(false));
            dispatch(profileSlice.actions.setToken(res.token));
            dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
            dispatch(loginSlice.actions.reset(null));
            dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
            dispatch(profileSlice.actions.setSessionState(false));
          }
          break;
        case EsiaResponseType.Registration:
          await storage.setItem(StorageKeys.isESIA, true);
          dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
          dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
          break;
      }
    } catch (e: any) {
      if (Array.isArray(e?.data) && e.data.length && e.data[0]?.code === 3) {
        dispatch(loginSlice.actions.setEsiaUserNotAccept(true));
        dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaLogin));
      } else {
        dispatch(loginSlice.actions.reset(null));
        dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
      }
    }
  };
};

export const esiaLoginFromActiveUser = (): AppThunk => {
  return async (dispatch, getState) => {
    const {code, state} = getState().login.esiaCode;
    let fcmToken: getTokenResponse | null = null;
    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.log(error);
    }

    const data: AuthorizeEsiaRequestPayload = {
      code: code!,
      state: state!,
      firebaseId: fcmToken?.token ?? null,
      previousFirebaseId: fcmToken?.previousToken ?? null,
    };

    try {
      const res = await httpService.post<AuthorizeEsiaResponsePayload>(
        getApiRoute(apiUrls.esiaLogin),
        data,
        new HttpWrapperOptions().WaitTimeout(0).Wait(true).ThrowException(false).Errors(false),
      );

      switch (res?.type) {
        case EsiaResponseType.Authorization:
          await storage.setItem(StorageKeys.activationToken, res.token);
          await storage.setItem(StorageKeys.isESIA, true);
          dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
          break;
        case EsiaResponseType.Registration:
          dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
          dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
          break;
      }
    } catch (e: any) {}
  };
};

export const esiaRegistration = (regData: RegisterClientCabinetRequestPayload): AppThunk => {
  return async () => {
    await httpService.post(
      getApiRoute(apiUrls.esiaRegistration),
      regData,
      new HttpWrapperOptions().Wait(true).ThrowException(true),
    );
  };
};

export const esiaRegisterCardApplication = async (
  regData: RegisterCardApplicationRequestPayload,
  isShowError = true,
): Promise<RegisterCardApplicationResponsePayload> => {
  return httpService.post<RegisterCardApplicationResponsePayload>(
    getApiRoute(apiUrls.esiaRegisterCardApplication),
    regData,
    new HttpWrapperOptions().Errors(isShowError).Wait(true).ThrowException(true),
  );
};

export const esiaSendActivateCard = (payload: ActivateCardRequestPayload): AppThunk => {
  return async () => {
    const token = await storage.getItem(StorageKeys.activationToken);
    await httpService.post(
      getApiRoute(apiUrls.esiaActivateCard),
      payload,
      new HttpWrapperOptions()
        .Headers({Authorization: `Bearer ${token}`})
        .Wait(true)
        .ThrowException(true),
    );
  };
};

export const esiaSendActivateCardAfterLogged = (payload: ActivateCardRequestPayload): AppThunk => {
  return async () => {
    const token = await storage.getItem(StorageKeys.token);
    await httpService.post(
      getApiRoute(apiUrls.esiaActivateCard),
      payload,
      new HttpWrapperOptions()
        .Headers({Authorization: `Bearer ${token}`})
        .Wait(true)
        .ThrowException(true),
    );
  };
};
